import { Link, useParams } from "@tanstack/react-router";

import { useMemo } from "react";

import { useInstruments } from "../api/hooks/useInstruments";
import { useTrade } from "../api/hooks/useTrade";
import { FillTimelineChart } from "../components/trades/FillTimelineChart";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { formatMoney } from "../lib/money";
import { formatQuantity } from "../lib/tradeFormat";
import {
  computeLegPnls,
  computeTradeAnalyticsKpis,
  formatHoldDuration,
} from "../lib/tradeAnalytics";

type TradeAnalyticsParams = {
  tradeId: string;
};

function KpiCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-on-surface-variant">
        {label}
      </span>
      <span className="text-lg font-medium text-on-surface">{value}</span>
    </div>
  );
}

export function TradeAnalyticsPage() {
  const { tradeId } = useParams({ strict: false }) as TradeAnalyticsParams;
  const { data: trade, isLoading, isError, error } = useTrade(tradeId);
  // Resolve each leg's instrument UUID to its human-readable symbol. The
  // per-leg LegResponse carries only instrument_id; the symbol lives on the
  // instruments list, so we index it here and fall back to nothing when a
  // match is missing (e.g. a deleted instrument).
  const { data: instruments } = useInstruments({ include_deleted: true });
  const symbolById = useMemo(() => {
    const map = new Map<string, string>();
    for (const inst of instruments?.items ?? []) {
      map.set(inst.instrument_id, inst.symbol);
    }
    return map;
  }, [instruments]);

  if (isLoading) {
    return (
      <section className="p-6" role="status" aria-live="polite">
        <p className="text-on-surface-variant">Loading trade analytics…</p>
      </section>
    );
  }

  if (isError || trade === undefined) {
    return (
      <section className="p-6" role="alert">
        <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
          Trade not found
        </h1>
        <p className="mt-2 text-on-surface-variant">
          {error instanceof Error
            ? error.message
            : "The trade analytics could not be loaded."}
        </p>
        <Link to="/trades" className="mt-4 inline-block text-primary underline">
          Back to trades
        </Link>
      </section>
    );
  }

  const kpis = computeTradeAnalyticsKpis(trade);
  const legPnls = computeLegPnls(trade);
  const realizedReturnDisplay =
    kpis.realizedReturnPct === null ? "—" : `${kpis.realizedReturnPct}%`;
  const rMultipleDisplay =
    kpis.rMultiple === null ? "—" : `${kpis.rMultiple}R`;
  const plannedRiskDisplay =
    kpis.plannedRiskAmount === null
      ? "—"
      : formatMoney(kpis.plannedRiskAmount, {
          currency: kpis.plannedRiskCurrency ?? trade.currency,
        });
  const openingNotionalDisplay =
    kpis.openingNotionalAmount === null
      ? "—"
      : formatMoney(kpis.openingNotionalAmount, { currency: trade.currency });

  return (
    <section className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight text-on-surface">
            Trade analytics
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/trades/$tradeId/whatif"
            params={{ tradeId: trade.trade_id }}
            className="text-primary underline"
          >
            Try What If
          </Link>
          <Link
            to="/trades/$tradeId"
            params={{ tradeId: trade.trade_id }}
            className="text-primary underline"
          >
            Back to trade
          </Link>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Key metrics</CardTitle>
          <CardDescription>
            Derived from the trade aggregate. Returns are realized-only — open
            positions contribute zero to the realized P&amp;L.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6"
            data-testid="trade-analytics-kpi-bar"
          >
            <KpiCell
              label="Realized P&L"
              value={formatMoney(kpis.realizedPnlAmount, {
                currency: kpis.realizedPnlCurrency,
              })}
            />
            <KpiCell label="Realized %" value={realizedReturnDisplay} />
            <KpiCell
              label="Hold time"
              value={formatHoldDuration(kpis.holdDurationSeconds)}
            />
            <KpiCell label="R-multiple" value={rMultipleDisplay} />
            <KpiCell label="Opening notional" value={openingNotionalDisplay} />
            <KpiCell label="Planned risk" value={plannedRiskDisplay} />
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-on-surface">
          Per-leg breakdown ({trade.legs.length})
        </h2>
        {trade.legs.length === 0 ? (
          <p className="text-sm text-on-surface-variant">
            This trade has no legs.
          </p>
        ) : (
          <ul className="grid gap-4">
            {trade.legs.map((leg, idx) => {
              const summary = legPnls[idx];
              const symbol = symbolById.get(leg.instrument_id);
              return (
                <li key={leg.leg_id}>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">
                        Leg {leg.direction.toUpperCase()}{" "}
                        {formatQuantity(leg.target_qty)}
                      </CardTitle>
                      {symbol !== undefined ? (
                        <CardDescription>{symbol}</CardDescription>
                      ) : null}
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm md:grid-cols-4">
                        <dt className="font-medium text-on-surface-variant">
                          Realized P&L
                        </dt>
                        <dd className="text-on-surface">
                          {summary !== undefined
                            ? formatMoney(summary.realizedPnlAmount, {
                                currency: summary.realizedPnlCurrency,
                              })
                            : "—"}
                        </dd>

                        <dt className="font-medium text-on-surface-variant">
                          Opening notional
                        </dt>
                        <dd className="text-on-surface">
                          {summary !== undefined
                            ? formatMoney(summary.openingNotionalAmount, {
                                currency: trade.currency,
                              })
                            : "—"}
                        </dd>

                        <dt className="font-medium text-on-surface-variant">
                          Fills
                        </dt>
                        <dd className="text-on-surface">{leg.fills.length}</dd>

                        <dt className="font-medium text-on-surface-variant">
                          Lots
                        </dt>
                        <dd className="text-on-surface">{leg.lots.length}</dd>
                      </dl>
                      <FillTimelineChart fills={leg.fills} />
                    </CardContent>
                  </Card>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
